import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/20/solid";
import type { AuditScoreType } from "@ots-frontend/ots-data";
import { AuditScoreRing } from "../audit/AuditScoreRing";
import { OtsTooltip } from "../components/OtsTooltip";
import { ActivityAttachmentsBadge } from "../driver-activity/ActivityAttachmentsBadge";
import { ActivityGeofenceChip } from "../driver-activity/ActivityGeofenceChip";
import { ActivityEntryPreview } from "../driver-activity/ActivityEntryPreview";
import { ActivityStatusIcon } from "../driver-activity/ActivityStatusIcon";
import { formatActivityDateTime, formatActivityQuantity } from "../driver-activity/driver-activity-format";
import type { ActivityEntry } from "../driver-activity/model/activity-types";
import { containerTicketId, type ContainerTicketParty, haulerNameOf } from "./container-details-model";

export type ContainerTicketListProps = {
  entries: readonly ActivityEntry[];
  /** The tank the panel is about; a ticket delivering into it reads "from" rather than "to". */
  containerId: string;
  timeZone?: string;
  auditScoreType?: AuditScoreType;
  /** The ticket picked up for the drop being looked at, outlined. */
  highlightKey?: string;
  /** Whether each row names the hauler (HubGo) or the driver (DispatchGo). */
  party?: ContainerTicketParty;
  /** Opens the ticket's profile from its ID. */
  onOpenTicket?: (entry: ActivityEntry) => void;
  onOpenAudit?: (entry: ActivityEntry) => void;
  onOpenAttachments?: (entry: ActivityEntry) => void;
};

/**
 * A tank's tickets as compact two-line rows, built from the same Driver Activity pieces: status icon, ticket ID (the
 * resource ID, else the paper ticket number) with its preview card, geofence time on site, audit ring, and attachments.
 */
export const ContainerTicketList = ({
  entries,
  containerId,
  timeZone,
  auditScoreType = "BUSINESS_VALIDATION_CONFIDENCE",
  highlightKey,
  party = "driver",
  onOpenTicket,
  onOpenAudit,
  onOpenAttachments,
}: ContainerTicketListProps) => {
  if (!entries.length) {
    return <p className="py-6 text-center text-xs text-[var(--ots-text-muted)]">No tickets in this time.</p>;
  }
  return (
    <ul aria-label="Tickets" className="divide-y divide-[var(--ots-outline-variant)]">
      {entries.map((entry) => {
        const outbound = entry.pickup?.containerId === containerId;
        const stop = outbound ? entry.pickup : entry.delivery;
        const other = outbound ? entry.delivery : entry.pickup;
        const Direction = outbound ? ArrowRightIcon : ArrowLeftIcon;
        const id = containerTicketId(entry) ?? "—";
        const geofence = outbound ? entry.pickupGeofence : entry.deliveryGeofence;
        const name = party === "hauler" ? haulerNameOf(stop) ?? "Unknown hauler" : stop?.personName ?? "Unknown driver";
        const idClasses = "truncate rounded font-mono text-[13px] font-semibold text-[var(--ots-primary)] outline-none focus-visible:ring-2 focus-visible:ring-[var(--ots-primary)]";
        return (
          <li
            key={entry.key}
            data-ticket={entry.key}
            className={[
              "grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-2 gap-y-0.5 px-1 py-2 text-xs",
              entry.key === highlightKey ? "rounded-md outline outline-2 -outline-offset-2 outline-[var(--ots-primary)]" : "",
            ].join(" ")}>
            <span className="row-span-2 flex items-center"><ActivityStatusIcon entry={entry}/></span>
            <span className="flex min-w-0 items-center gap-2">
              <OtsTooltip panelClassName="!max-w-none !p-3" placement="bottom"
                content={<ActivityEntryPreview entry={entry} timeZone={timeZone} auditScoreType={auditScoreType}/>}>
                {onOpenTicket && entry.tta
                  ? <button type="button" className={[idClasses, "hover:underline"].join(" ")} onClick={() => onOpenTicket(entry)}>{id}</button>
                  : <span tabIndex={0} className={idClasses}>{id}</span>}
              </OtsTooltip>
              <span className="shrink-0 tabular-nums text-[var(--ots-text-muted)]">{formatActivityDateTime(entry.time, timeZone)}</span>
              <ActivityGeofenceChip match={geofence} ticketTime={stop?.createDttm} timeZone={timeZone}/>
            </span>
            <span className="flex items-center justify-end gap-2">
              <span className="font-semibold tabular-nums">{formatActivityQuantity(stop?.quantityDecimal, stop?.quantityUnitOfMeasure)}</span>
              {entry.tta && <AuditScoreRing audit={entry.audit} scoreType={auditScoreType} timeZone={timeZone} onClick={onOpenAudit && (() => onOpenAudit(entry))}/>}
            </span>
            <span className="flex min-w-0 items-center gap-1 text-[var(--ots-text-muted)]">
              <span className="truncate">{name}</span>
              <Direction aria-label={outbound ? "to" : "from"} className="size-3 shrink-0"/>
              <span className="truncate">{other?.siteName ?? (outbound ? "Not delivered yet" : "Unknown pickup")}</span>
            </span>
            <span className="flex justify-end">
              <ActivityAttachmentsBadge attachments={entry.attachments} onOpen={onOpenAttachments && (() => onOpenAttachments(entry))}/>
            </span>
          </li>
        );
      })}
    </ul>
  );
};
